import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} text-center mb-8 md:mb-12`}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
          <i className="ri-sparkling-line text-primary-700" />
        </div>
        <span className="text-xs font-semibold text-accent-600 tracking-wider uppercase">{label}</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">{title}</h2>
      {subtitle && <p className="mt-3 text-sm text-foreground-600">{subtitle}</p>}
      <div className="w-12 h-1 bg-accent-500 mx-auto mt-3 rounded-full" />
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  const delayClass = delay === 0 ? '' : `scroll-reveal-delay-${delay}`;
  return (
    <div ref={ref} className={`scroll-reveal ${delayClass} ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

/* 動態數字計數器 */
function CountUp({ end, suffix = '', duration = 2000 }: { end: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.floor(eased * end);
      setCount(start);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

import { useEffect, useRef, useState } from 'react';

/* 背景粒子 */
function FloatingParticles() {
  const particles = Array.from({ length: 10 }, (_, i) => ({
    id: i,
    left: `${10 + (i * 8) % 80}%`,
    top: `${15 + (i * 11) % 65}%`,
    size: 3 + (i % 3) * 3,
    delay: i * 0.8,
    duration: 5 + (i % 3) * 2,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full bg-accent-400/15 animate-float"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function AsiaSummitPage() {
  const navigate = useNavigate();

  const topics = [
    {
      icon: 'ri-brain-line',
      title: '聽力與認知健康',
      desc: '探討聽力損失與認知退化、失智症之間的關聯，以及早期助聽器介入如何延緩認知衰退。',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '慢性病與聽力共病',
      desc: '聚焦糖尿病、高血壓、心血管疾病等慢性病與聽力損失的雙向關係，推動跨科整合照護。',
    },
    {
      icon: 'ri-robot-2-line',
      title: 'AI 助聽技術革新',
      desc: '分享人工智慧在助聽器降噪、語音分離、自動場景辨識等領域的最新突破與臨床應用。',
    },
    {
      icon: 'ri-group-line',
      title: '聽力平權政策',
      desc: '各國聽力保健政策比較、聽損者就業權益、無障礙環境立法，以及社會企業責任實踐。',
    },
    {
      icon: 'ri-user-3-line',
      title: '兒童聽力早期療育',
      desc: '新生兒聽篩普及率、早療黃金期、聽損兒童教育權益，以及家庭支持系統建構。',
    },
    {
      icon: 'ri-earth-line',
      title: '亞洲聽力數據網絡',
      desc: '建立跨國聽力健康數據共享平台，推動標準化篩檢工具與區域流行病學研究合作。',
    },
  ];

  const agenda = [
    {
      day: 'Day 1',
      date: '2026.10.15',
      title: '開幕與政策論壇',
      items: [
        '09:00 — 開幕致詞：共聽生活協會理事長',
        '10:00 — 專題演講：亞洲聽力健康現況與挑戰',
        '11:30 — 圓桌論壇：各國聽力政策比較',
        '14:00 — 分論壇：聽力平權與社會共融',
        '16:00 — 企業CSR實踐案例分享',
      ],
    },
    {
      day: 'Day 2',
      date: '2026.10.16',
      title: '臨床與技術研討',
      items: [
        '09:00 — 專題演講：AI 助聽技術的未來',
        '10:30 — 臨床研究發表：聽力與慢性病共病',
        '13:30 — 工作坊：助聽器選配最新技術',
        '15:00 — 分論壇：兒童聽力早期療育',
        '17:00 — 亞洲聽力師認證制度研討',
      ],
    },
    {
      day: 'Day 3',
      date: '2026.10.17',
      title: '社群與未來展望',
      items: [
        '09:00 — 偏鄉聽力義診經驗分享',
        '10:30 — 聽損者生活品質研究報告',
        '13:30 — 青年創新提案競賽',
        '15:30 — 閉幕論壇：邁向 2030 聽力平權願景',
        '17:00 — 頒獎與閉幕式',
      ],
    },
  ];

  const speakers = [
    {
      name: '陳明華 教授',
      title: '國立台灣大學 耳鼻喉科主任',
      topic: '聽力與認知退化：10 年縱貫研究',
      country: '台灣',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20doctor%20in%20white%20coat%20portrait%20warm%20lighting%20clean%20background%20professional%20headshot%20photography%20confident%20smile&width=400&height=400&seq=100&orientation=squarish',
    },
    {
      name: 'Dr. Kenji Tanaka',
      title: '東京聽力研究所 所長',
      topic: '日本高齡化社會的聽力保健策略',
      country: '日本',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20male%20researcher%20portrait%20warm%20lighting%20clean%20background%20professional%20headshot%20photography%20wearing%20glasses%20confident&width=400&height=400&seq=101&orientation=squarish',
    },
    {
      name: 'Dr. Soo-Min Park',
      title: '首爾大學 聽力科學中心 主任',
      topic: 'AI 驅動的助聽器技術革新',
      country: '韓國',
      image: 'https://readdy.ai/api/search-image?query=professional%20korean%20female%20scientist%20portrait%20warm%20lighting%20clean%20background%20professional%20headshot%20photography%20confident%20expression&width=400&height=400&seq=102&orientation=squarish',
    },
    {
      name: 'Dr. Wei Ling Chen',
      title: '新加坡國立大學 聽力學教授',
      topic: '多元文化下的聽力服務模式',
      country: '新加坡',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20professor%20portrait%20warm%20lighting%20clean%20background%20professional%20headshot%20photography%20friendly%20smile%20modern&width=400&height=400&seq=103&orientation=squarish',
    },
    {
      name: 'Dr. Ahmad Ismail',
      title: '馬來西亞聽力協會 理事長',
      topic: '東南亞聽力資源整合與教育計畫',
      country: '馬來西亞',
      image: 'https://readdy.ai/api/search-image?query=professional%20southeast%20asian%20male%20doctor%20portrait%20warm%20lighting%20clean%20background%20professional%20headshot%20photography%20confident%20warm%20smile&width=400&height=400&seq=104&orientation=squarish',
    },
    {
      name: 'Dr. Supicha Wong',
      title: '曼谷聽力中心 醫學總監',
      topic: '熱帶氣候對助聽設備的挑戰',
      country: '泰國',
      image: 'https://readdy.ai/api/search-image?query=professional%20thai%20female%20audiologist%20portrait%20warm%20lighting%20clean%20background%20professional%20headshot%20photography%20confident%20expression&width=400&height=400&seq=105&orientation=squarish',
    },
  ];

  const pastStats = [
    { num: 3, suffix: '屆', label: '歷屆峰會', icon: 'ri-trophy-line' },
    { num: 24, suffix: '國', label: '參與國家', icon: 'ri-global-line' },
    { num: 180, suffix: '+', label: '演講場次', icon: 'ri-mic-line' },
    { num: 3500, suffix: '+', label: '與會人次', icon: 'ri-user-line' },
  ];

  const partners = [
    '國立台灣大學醫學院', '東京聽力研究所', '首爾大學聽力科學中心', '新加坡國立大學',
    '馬來西亞聽力協會', '泰國聽力學會', '世界衛生組織西太平洋區', '亞太聽力學聯盟',
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* ===== Hero ===== */}
        <div className="relative overflow-hidden text-white pt-6 md:pt-10 pb-8 md:pb-12">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-950" />
          <div className="absolute -top-20 -right-20 w-72 h-72 md:w-96 md:h-96 rounded-full bg-accent-400/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-primary-400/20 blur-2xl" />
          <div className="absolute top-10 left-10 w-20 h-20 md:w-32 md:h-32 rounded-full bg-accent-400/10 blur-2xl" />
          <FloatingParticles />

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center pt-8 md:pt-12 pb-6 md:pb-8">
            <span className="inline-block text-xs font-semibold tracking-widest text-accent-400 uppercase mb-3">
              Asia Hearing Health Summit
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight">
              亞洲聽力健康峰會
            </h1>
            <p className="mt-2 text-lg md:text-xl font-medium text-accent-400">
              2026 年度論壇
            </p>
            <p className="mt-4 text-white/80 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              亞洲規模最大的聽力健康學術與產業交流盛會。匯集 8 國專家學者、醫療機構與產業領袖，
              共同探討聽力科學、慢性病共病、AI 技術與聽力平權政策，推動亞洲聽力健康生態系發展。
            </p>

            {/* 日期 & 地點 & 報名 */}
            <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-5 py-3 border border-white/20 backdrop-blur-sm">
                <i className="ri-calendar-line text-accent-400 text-lg" />
                <span className="text-sm font-medium">2026.10.15 — 10.17</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-5 py-3 border border-white/20 backdrop-blur-sm">
                <i className="ri-map-pin-line text-accent-400 text-lg" />
                <span className="text-sm font-medium">台北國際會議中心</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-5 py-3 border border-white/20 backdrop-blur-sm">
                <i className="ri-earth-line text-accent-400 text-lg" />
                <span className="text-sm font-medium">8 國聯合主辦</span>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              
              {/* <button
                type="button"
                onClick={() => navigate('/booking')}
                className="bg-white text-primary-800 px-8 py-3.5 rounded-xl text-sm font-bold hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-ticket-line" />
                立即報名參加
              </button> */}
              
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('agenda-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border border-white/30 text-white px-8 py-3.5 rounded-xl text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-calendar-event-line" />
                查看完整議程
              </button>
            </div>
          </div>

          <svg className="absolute bottom-0 left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
          </svg>
        </div>

        {/* ===== 峰會簡介 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 items-center">
            <SectionReveal>
              <div className="relative rounded-2xl overflow-hidden h-64 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=international%20medical%20conference%20hall%20with%20audience%20professional%20speakers%20on%20stage%20modern%20venue%20warm%20lighting%20green%20and%20orange%20accent%20tones%20professional%20event%20photography%20wide%20angle&width=800&height=500&seq=110&orientation=landscape"
                  alt="亞洲聽力健康峰會現場"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-primary-900/20" />
              </div>
            </SectionReveal>
            <div>
              <SectionReveal>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-sparkling-line text-primary-700" />
                  </div>
                  <span className="text-xs font-semibold text-accent-600 tracking-wider uppercase">About the Summit</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  亞洲聽力健康的年度指標盛會
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  亞洲聽力健康峰會由共聽協會理事長李炫逸發起，每年邀請亞洲各國聽力專家、醫療機構、政策制定者、
                  助聽器產業與社會企業齊聚一堂。峰會涵蓋學術研究、臨床實務、技術創新與政策倡議四大面向，
                  是亞洲聽力健康領域最具影響力的交流平台。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  2026 年度峰會將以「從篩檢到共融——建構亞洲聽力健康生態系」為主題，
                  聚焦聽力與慢性病共病、AI 助聽技術、聽力平權政策與兒童早療等關鍵議題。
                </p>
                <div className="flex flex-wrap gap-2">
                  {['學術研究', '臨床實務', '技術創新', '政策倡議', '產業交流', '社會共融'].map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                      {tag}
                    </span>
                  ))}
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="background-100" />
          </svg>
        </div>

        {/* ===== 核心議題 ===== */}
        <div className="bg-background-100 py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader
              label="Core Topics"
              title="2026 核心議題"
              subtitle="六大主題深入探討聽力健康的科學、技術與社會面向"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {topics.map((item, idx) => (
                <SectionReveal key={item.title} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-background-200 p-6 hover:border-accent-300 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                      <i className={`${item.icon} text-xl text-primary-700`} />
                    </div>
                    <h3 className="text-base font-bold text-foreground-900">{item.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="background-100" />
          </svg>
        </div>

        {/* ===== 議程亮點 ===== */}
        <div id="agenda-section" className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader
            label="Agenda"
            title="議程亮點"
            subtitle="三天豐富議程，從政策論壇到技術研討再到社群共融"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {agenda.map((day, idx) => (
              <SectionReveal key={day.day} delay={idx}>
                <div className="bg-white rounded-2xl border border-background-200 overflow-hidden h-full flex flex-col">
                  <div className="bg-primary-800 px-5 py-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                      <i className="ri-calendar-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">{day.day}</div>
                      <div className="text-xs text-white/70">{day.date}</div>
                    </div>
                  </div>
                  <div className="p-5 flex-1">
                    <h3 className="text-base font-bold text-foreground-900 mb-4">{day.title}</h3>
                    <div className="space-y-3">
                      {day.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                          <span className="text-sm text-foreground-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="background-100" />
          </svg>
        </div>

        {/* ===== 演講嘉賓 ===== */}
        <div className="bg-background-100 py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader
              label="Keynote Speakers"
              title="重量級演講嘉賓"
              subtitle="來自亞洲各國的頂尖聽力專家學者"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {speakers.map((speaker, idx) => (
                <SectionReveal key={speaker.name} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-background-200 overflow-hidden hover:border-accent-300 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start gap-4 p-5">
                      <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 border-2 border-background-200">
                        <img src={speaker.image} alt={speaker.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-foreground-900">{speaker.name}</h3>
                          <span className="text-[10px] text-accent-700 font-medium bg-accent-100 px-1.5 py-0.5 rounded">{speaker.country}</span>
                        </div>
                        <p className="text-xs text-foreground-500 mt-0.5">{speaker.title}</p>
                        <p className="text-xs text-foreground-700 mt-2 leading-relaxed">
                          <span className="text-accent-600 font-medium">演講主題：</span>
                          {speaker.topic}
                        </p>
                      </div>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="background-100" />
          </svg>
        </div>

        {/* ===== 歷屆回顧 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader
            label="Past Editions"
            title="歷屆峰會回顧"
            subtitle="從 2023 年首屆到 2025 年，峰會規模逐年擴大，影響力遍及全亞洲"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {pastStats.map((stat, idx) => (
              <SectionReveal key={stat.label} delay={idx}>
                <div className="bg-white rounded-2xl border border-background-200 p-6 text-center hover:border-accent-300 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-xl text-primary-700`} />
                  </div>
                  <div className="text-3xl font-bold text-foreground-900">
                    <CountUp end={stat.num} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-foreground-600 mt-1">{stat.label}</div>
                </div>
              </SectionReveal>
            ))}
          </div>

          {/* 歷屆年份標記 */}
          <SectionReveal className="mt-8 md:mt-10">
            <div className="relative">
              <div className="hidden md:block absolute left-0 right-0 top-1/2 h-px bg-background-200" />
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-0 relative">
                {[
                  { year: '2023', title: '首屆峰會', desc: '台北，6 國參與' },
                  { year: '2024', title: '擴大規模', desc: '東京，12 國參與' },
                  { year: '2025', title: '線上線下', desc: '首爾，18 國參與' },
                  { year: '2026', title: '年度盛會', desc: '台北，24 國參與' },
                ].map((item, idx) => (
                  <div key={item.year} className="flex md:flex-col items-center gap-3 md:gap-2 md:text-center bg-background-50 md:bg-transparent px-4 py-2 md:px-0 md:py-0 rounded-xl">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${idx === 3 ? 'bg-accent-500' : 'bg-primary-100'}`}>
                      <i className={`ri-circle-fill text-xs ${idx === 3 ? 'text-white' : 'text-primary-400'}`} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-foreground-900">{item.year} · {item.title}</div>
                      <div className="text-xs text-foreground-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionReveal>
        </div>

        {/* ===== 主辦與協辦機構 ===== */}
        <div className="bg-background-100 py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionReveal>
              <div className="text-center mb-8">
                <span className="text-xs font-semibold text-accent-600 tracking-wider uppercase">Partners</span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mt-2">主辦與協辦機構</h2>
              </div>
            </SectionReveal>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {partners.map((partner) => (
                <SectionReveal key={partner}>
                  <div className="bg-white rounded-xl border border-background-200 px-5 py-3 text-sm text-foreground-700 font-medium hover:border-accent-300 transition-all duration-200">
                    {partner}
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* ===== CTA ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="bg-primary-800 rounded-2xl overflow-hidden p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8 relative">
              <div className="absolute top-0 right-0 w-40 h-40 md:w-60 md:h-60 rounded-full bg-accent-400/10 blur-2xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 md:w-48 md:h-48 rounded-full bg-primary-400/10 blur-2xl" />
              <div className="relative flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                    <i className="ri-ticket-line text-white" />
                  </div>
                  <span className="text-xs font-semibold text-accent-400 tracking-wider uppercase">Registration</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  加入 2026 亞洲聽力健康峰會
                </h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  早鳥報名現正開放中！與亞洲頂尖聽力專家面對面交流，掌握聽力健康領域最新趨勢與技術。
                  學生、醫療從業人員與協會會員享專屬優惠。
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-white/60">
                  <span className="flex items-center gap-1"><i className="ri-calendar-line text-accent-400" /> 2026.10.15 — 17</span>
                  <span className="flex items-center gap-1"><i className="ri-map-pin-line text-accent-400" /> 台北國際會議中心</span>
                  <span className="flex items-center gap-1"><i className="ri-group-line text-accent-400" /> 預計 1,500+ 與會者</span>
                </div>
              </div>
              <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/booking')}
                  className="bg-white text-primary-800 px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-background-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <i className="ri-ticket-line" />
                  立即報名
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="border border-white/30 text-white px-6 md:px-8 py-3 rounded-xl text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <i className="ri-mail-line" />
                  團體報名洽詢
                </button>
              </div>
            </div>
          </SectionReveal>

          {/* 相關連結 */}
          <SectionReveal className="mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-foreground-600">
              <span>相關連結：</span>
              <button
                type="button"
                onClick={() => navigate('/association')}
                className="text-primary-700 hover:text-primary-900 font-medium flex items-center gap-1 transition-colors"
              >
                共聽生活協會 <i className="ri-arrow-right-line" />
              </button>
              <span className="hidden sm:inline text-foreground-300">|</span>
              <button
                type="button"
                onClick={() => navigate('/knowledge')}
                className="text-primary-700 hover:text-primary-900 font-medium flex items-center gap-1 transition-colors"
              >
                聽力知識專區 <i className="ri-arrow-right-line" />
              </button>
              <span className="hidden sm:inline text-foreground-300">|</span>
              <button
                type="button"
                onClick={() => navigate('/hearing-chronic-disease')}
                className="text-primary-700 hover:text-primary-900 font-medium flex items-center gap-1 transition-colors"
              >
                聽損與慢性病研究 <i className="ri-arrow-right-line" />
              </button>
            </div>
          </SectionReveal>
        </div>
      </main>

      <Footer />
    </div>
  );
}